import { useState, useMemo } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useTasks,
  useUpdateTask,
  TaskState,
  TaskWithRelations,
} from "@/hooks/useTasks";
import { usePeople } from "@/hooks/usePeople";
import { TaskColumn } from "@/components/tasks/TaskColumn";
import { TaskDialog } from "@/components/tasks/TaskDialog";

interface ProjectTasksTabProps {
  project: {
    id: string;
    name: string;
  };
}

export function ProjectTasksTab({ project }: ProjectTasksTabProps) {
  const { data: tasks = [], isLoading: tasksLoading } = useTasks();
  const { data: people = [], isLoading: peopleLoading } = usePeople("employee");
  const updateTask = useUpdateTask();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithRelations | null>(
    null,
  );
  const [defaultAssigneeId, setDefaultAssigneeId] = useState<string | null>(
    null,
  );
  const [defaultState, setDefaultState] = useState<TaskState>("backlog");
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);

  // Filter tasks for this project
  const projectTasks = useMemo(() => {
    return tasks.filter((task) =>
      task.projects?.some((p) => p.project?.id === project.id),
    );
  }, [tasks, project.id]);

  // Group tasks by person, then by state
  const tasksByPerson = useMemo(() => {
    const grouped: Record<string, Record<TaskState, TaskWithRelations[]>> = {};

    // Initialize with unassigned
    grouped["unassigned"] = {
      backlog: [],
      todo: [],
      in_progress: [],
      done: [],
    };

    // Initialize for each person
    people.forEach((person) => {
      grouped[person.id] = {
        backlog: [],
        todo: [],
        in_progress: [],
        done: [],
      };
    });

    // Group tasks
    projectTasks.forEach((task) => {
      if (task.assignees && task.assignees.length > 0) {
        task.assignees.forEach((assignee) => {
          const personId = assignee.person?.id;
          if (personId && grouped[personId]) {
            grouped[personId][task.state].push(task);
          }
        });
      } else {
        grouped["unassigned"][task.state].push(task);
      }
    });

    return grouped;
  }, [projectTasks, people]);

  // Build person list for display
  const personList = useMemo(() => {
    const list: Array<{
      id: string;
      name: string;
      tasks: Record<TaskState, TaskWithRelations[]>;
    }> = [];

    // Add unassigned first if there are any unassigned tasks
    const unassignedTasks = tasksByPerson["unassigned"];
    const hasUnassigned = Object.values(unassignedTasks).some(
      (arr) => arr.length > 0,
    );
    if (hasUnassigned) {
      list.push({
        id: "unassigned",
        name: "Unassigned",
        tasks: unassignedTasks,
      });
    }

    // Add people who have tasks in this project
    people.forEach((person) => {
      const personTasks = tasksByPerson[person.id];
      const hasTasks =
        personTasks && Object.values(personTasks).some((arr) => arr.length > 0);
      if (hasTasks) {
        list.push({
          id: person.id,
          name: `${person.first_name} ${person.last_name}`,
          tasks: personTasks,
        });
      }
    });

    return list;
  }, [tasksByPerson, people]);

  const handleDragStart = (taskId: string) => {
    setDraggedTaskId(taskId);
  };

  const handleDrop = async (newState: TaskState) => {
    if (draggedTaskId) {
      await updateTask.mutateAsync({ id: draggedTaskId, state: newState });
      setDraggedTaskId(null);
    }
  };

  const handleEditTask = (task: TaskWithRelations) => {
    setEditingTask(task);
    setDefaultAssigneeId(null);
    setDefaultState("backlog");
    setDialogOpen(true);
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setDefaultAssigneeId(null);
    setDefaultState("backlog");
    setDialogOpen(true);
  };

  const handleCreateTaskForPerson = (
    personId: string,
    state: TaskState = "backlog",
  ) => {
    setEditingTask(null);
    setDefaultAssigneeId(personId);
    setDefaultState(state);
    setDialogOpen(true);
  };

  if (tasksLoading || peopleLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Tasks</h3>
        <Button offlineEdit onClick={handleCreateTask} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Task
        </Button>
      </div>

      {personList.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No tasks for this project yet. Create one to get started.
        </div>
      ) : (
        <div className="flex overflow-x-auto -mx-6 px-6">
          <div className="flex min-w-max">
            {personList.map((person, index) => (
              <TaskColumn
                key={person.id}
                personId={person.id}
                personName={person.name}
                tasksByState={person.tasks}
                onDragStart={handleDragStart}
                onDrop={handleDrop}
                onEditTask={handleEditTask}
                onCreateTask={handleCreateTaskForPerson}
                isDragging={draggedTaskId !== null}
                isLast={index === personList.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        task={editingTask}
        defaultAssigneeId={defaultAssigneeId}
        defaultProjectId={project.id}
        defaultState={defaultState}
      />
    </div>
  );
}
